import {
	BackendConfig,
	BackendProvider,
	DISABLED_PROVIDER_WEBSOCKET_CONFIG,
	IBackendAdapter,
	IProviderWebSocketProtocol,
	ProviderWebSocketConfig, ProviderCapabilities } from './types'
import {
	PlaceOrModifyOrdersRequest,
	PlaceOrModifyOrdersResponse,
	GetAllOrdersRequest,
	CancelOrdersRequest,
	CancelOrdersResponse,
	Holding,
	TradingAccountMargin,
	GetTradingAccountMarginRequest
} from '../proto/generated/trading-bridge'
import { Order, MarginFromBrokerRequest, MarginFromBrokerResponse } from '../proto/generated/order'
import { Security } from '../proto/generated/security'
import { PositionRequest, PositionResponse } from '../proto/generated/position'
import {
	AuthProviderLoginUrlResponse,
	ForgetPasswordRequest,
	GetActiveSessionsResponse,
	LoginRequest,
	LoginResponse,
	LoginWithMobileRequest,
	LoginWithMobileResponse,
	OperationResponse,
	User,
	UserSignUpRequest,
	ValidateThirdPartyTokenRequest,
	VerifyMobileLoginRequest
} from '../proto/generated/identity'

export abstract class BaseAdapter implements IBackendAdapter {
	protected config: BackendConfig

	constructor(config: BackendConfig) {
		this.config = config
	}

	getProvider(): BackendProvider {
		return this.config.provider
	}

	getCapabilities(): ProviderCapabilities {
		return this.config.capabilities
	}

	getBaseUrl(): string {
		return this.config.baseUrl
	}

	getProviderUrl(): string {
		return this.config.providerUrl || this.config.baseUrl
	}

	getWebSocketUrl(): string {
		return this.config.wsUrl || this.config.baseUrl.replace('http', 'ws')
	}

	/**
	 * Extra provider websocket. Disabled unless the concrete adapter/config opts in
	 * (tick2trade: disabled; broker providers enable it).
	 */
	getProviderWebSocketConfig(): ProviderWebSocketConfig {
		return this.config.providerWebSocket ?? DISABLED_PROVIDER_WEBSOCKET_CONFIG
	}

	getProviderWebSocketProtocol(): IProviderWebSocketProtocol | null {
		return null
	}

	abstract transformRequest<T = any>(
		endpoint: string,
		data: T,
		method: string
	): {
		url: string
		data: any
		headers?: Record<string, string>
	}

	abstract transformResponse<T = any>(response: any, endpoint: string): T

	transformError(error: any): any {
		return {
			message: error?.message || 'An error occurred',
			code: error?.code || 'UNKNOWN_ERROR',
			status: error?.response?.status || 500,
			data: error?.response?.data
		}
	}

	protected getCustomHeaders(): Record<string, string> {
		return this.config.headers || {}
	}

	// Abstract order methods - must be implemented by concrete adapters
	abstract placeOrModifyOrder(request: PlaceOrModifyOrdersRequest): Promise<PlaceOrModifyOrdersResponse>
	abstract getOrders(request: GetAllOrdersRequest): Promise<Order[]>
	abstract cancelOrder(request: CancelOrdersRequest): Promise<CancelOrdersResponse>
	abstract getPositions(request: PositionRequest): Promise<PositionResponse>
	abstract getPlacedOrder(params: any): Promise<Order>
	abstract genrateLocalSecurityMap(security: Security): void
	abstract getHoldings(accountId: string | undefined): Promise<Holding[]>
	abstract syncTradingAccountMargin(request: GetTradingAccountMarginRequest): Promise<TradingAccountMargin>
	abstract getRequiredMargin(request: MarginFromBrokerRequest): Promise<MarginFromBrokerResponse>

	// Abstract auth methods - must be implemented by concrete adapters
	abstract getLoginUrl(body: any): Promise<AuthProviderLoginUrlResponse>
	abstract loginWithMail(body: LoginRequest): Promise<LoginResponse>
	abstract signUpWithMail(userSignUpRequest: UserSignUpRequest): Promise<LoginResponse>
	abstract loginWithMobile(body: LoginWithMobileRequest): Promise<LoginWithMobileResponse>
	abstract verifyMobileLogin(body: VerifyMobileLoginRequest): Promise<LoginResponse>
	abstract sendEmailOtp(email: string, options?: { purpose?: string }): Promise<any>
	abstract forgetPassword(body: ForgetPasswordRequest): Promise<User>
	abstract validateToken(body: ValidateThirdPartyTokenRequest): Promise<LoginResponse>

	abstract logout(): void
	abstract getActiveSessions(): Promise<GetActiveSessionsResponse>
	abstract revokeSession(deviceId: string): Promise<OperationResponse>
}
